import { forwardRef } from 'react';

import { Header, HeaderGroup, flexRender } from '@tanstack/react-table';
import { Group, MantineTable, Stack, Tooltip } from 'ui';
import { IconChevronDown, IconChevronUp } from 'ui/Icons';

import { useTableContext } from '../../table.context';
import { getCommonCellStyles } from '../utils';

type TableHeaderCellProps = {
  header: Header<unknown, unknown>;
};

const TableHeaderCell = forwardRef<HTMLTableCellElement, TableHeaderCellProps>(
  ({ header }, ref) => {
    const { styles } = getCommonCellStyles({ column: header.column });

    return (
      <MantineTable.Th
        ref={ref}
        onClick={() => {
          if (header.column.getCanSort()) {
            // Reset to the header default sorting

            header.column.toggleSorting();
          }
        }}
        colSpan={header.colSpan}
        style={{
          width: header.getSize(),
          cursor: header.column.getCanSort() ? 'pointer' : undefined,
          ...styles,
        }}
      >
        <Group wrap="nowrap">
          {header.isPlaceholder ? null : (
            <div style={{ whiteSpace: 'nowrap' }}>
              {flexRender(header.column.columnDef.header, header.getContext())}
            </div>
          )}
          {header.column.getCanSort() && (
            <Stack gap={0}>
              <IconChevronUp
                size="8px"
                color={header.column.getIsSorted() === 'asc' ? '#234DD4' : '#CCCCCC'}
                stroke={4}
              />
              <IconChevronDown
                size="8px"
                color={header.column.getIsSorted() === 'desc' ? '#234DD4' : '#CCCCCC'}
                stroke={4}
              />
            </Stack>
          )}
        </Group>
      </MantineTable.Th>
    );
  }
);
TableHeaderCell.displayName = 'TableHeaderCell';
const TableHead = () => {
  const { table } = useTableContext();
  const isPdf = table.options.meta?.isPdf;
  return (
    <MantineTable.Thead>
      {table.getHeaderGroups().map((headerGroup: HeaderGroup<unknown>) => (
        <MantineTable.Tr key={headerGroup.id}>
          {headerGroup.headers.map((header: Header<unknown, unknown>) => {
            const tooltip = header.column?.columnDef?.meta?.headerTooltip;

            if (header.column?.columnDef?.meta?.isRenderable === false && !isPdf) return null;

            return tooltip ? (
              <Tooltip key={header.id} label={tooltip} position="bottom" w="300px">
                <TableHeaderCell header={header} />
              </Tooltip>
            ) : (
              <TableHeaderCell key={header.id} header={header} />
            );
          })}
        </MantineTable.Tr>
      ))}
    </MantineTable.Thead>
  );
};

export { TableHead };
